import { createContext, useContext, useState, useEffect } from 'react';
import { authFetch } from '../utils/auth';
import { getAcquisitionHeaders, clearAcquisition } from '../utils/acquisition';
import { msalInstance } from '../auth/msalConfig';
import { handleAutoMigration, getMigrationNotification } from '../utils/profileMigration';

const ProfileContext = createContext();
export const useProfile = () => useContext(ProfileContext);

export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [loaded,  setLoaded]  = useState(false);
  const [migrationStatus, setMigrationStatus] = useState({
    isRunning: false,
    completed: false,
    result: null
  });

  const refresh = () =>
    new Promise(res => {
      setLoaded(false);
      // The landing-URL acquisition record (utils/acquisition.js) rides on
      // this first authenticated call; the backend stamps it only when it
      // creates the Profile and ignores it for an existing one. Cleared once
      // a profile response is in — either way it has done its job.
      authFetch('/profile', { headers: getAcquisitionHeaders() })
        .then(r => r.json())
        .then(json => { 
          clearAcquisition();
          setProfile(json); 
          setLoaded(true); 
          res(json); 
        })
        .catch((error) => { 
          console.error('Error loading profile:', error);
          setProfile({}); 
          setLoaded(true); 
          res({}); 
        });
    });

  // Handle automatic migration when profile is loaded
  useEffect(() => {
    if (!loaded || !profile) return;

    const runMigration = async () => {
      setMigrationStatus(prev => ({ ...prev, isRunning: true }));
      
      const migrationResult = await handleAutoMigration(profile);
      
      setMigrationStatus({
        isRunning: false,
        completed: true,
        result: migrationResult
      });
    };

    // Only run if migration hasn't been completed yet
    if (!migrationStatus.completed) {
      runMigration();
    }
  }, [profile, loaded, migrationStatus.completed]);

  // initial load — only fetch if user is already authenticated
  useEffect(() => {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (account) {
      refresh();
    } else {
      // Not authenticated — mark as loaded with empty profile
      setProfile({});
      setLoaded(true);
    }
  }, []);

  return (
    <ProfileContext.Provider value={{ 
      profile, 
      loaded, 
      refresh, 
      setProfile,
      migrationStatus,
      getMigrationNotification: () => getMigrationNotification(migrationStatus.result)
    }}>
      {children}
    </ProfileContext.Provider>
  );
}
